import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "./Navbar";
import API_URL from "./config";
import FreeAgencyRecap from "./FreeAgencyRecap";
import "./FreeAgents.css";

const avg = (arr) => arr.reduce((a, b) => a + b, 0) / arr.length;
const playerOverall = (p) => {
  const offense  = avg([p.close, p.mid, p.three, p.free_throw, p.dunk, p.passing, p.dribbling, p.offensive_iq].map(v => v || 50));
  const defense  = avg([p.defensive_iq, p.rebounding, p.strength, p.jumping].map(v => v || 50));
  const athletic = avg([p.speed, p.stamina, p.jumping].map(v => v || 50));
  return Math.round(offense * 0.45 + defense * 0.35 + athletic * 0.2);
};

export default function FreeAgents() {
  const navigate = useNavigate();
  const [agents, setAgents]       = useState([]);
  const [loading, setLoading]     = useState(true);
  const [search, setSearch]       = useState("");
  const [posFilter, setPosFilter] = useState("ALL");
  const [isBiddingOpen, setIsBiddingOpen] = useState(false);
  const [expandedPlayer, setExpandedPlayer] = useState(null);
  const [submitting, setSubmitting] = useState({});
  const [pendingConfirmation, setPendingConfirmation] = useState(null);
  const [finalizing, setFinalizing] = useState(false);
  const [capInfo, setCapInfo] = useState(null);
  const [rosterInfo, setRosterInfo] = useState(null);
  const [gamePhase, setGamePhase] = useState(null);
  const [recapData, setRecapData] = useState(null);

  const activeSaveId = localStorage.getItem("activeSaveId");
  const user = JSON.parse(localStorage.getItem("user") || "{}");

  const loadStatus = async () => {
    try {
      // First, get save info to determine game phase
      const saveRes = await fetch(`${API_URL}/api/saves/${activeSaveId}`, {
        headers: { "X-User-ID": user._id }
      });
      const saveData = await saveRes.json();
      
      // currentPhase is directly on the save object, not under lifecycle
      const phase = saveData?.save?.currentPhase;
      setGamePhase(phase);

      // Load appropriate endpoint based on phase
      let endpoint;
      if (phase === "offseason") {
        // NEW offseason free agency system
        endpoint = `/api/saves/${activeSaveId}/free-agency/offseason-players`;
      } else if (phase === "regular_season") {
        endpoint = `/api/saves/${activeSaveId}/mid-season-free-agents`;
      } else {
        setLoading(false);
        return;
      }

      const res = await fetch(`${API_URL}${endpoint}`, {
        headers: { "X-User-ID": user._id }
      });
      const data = await res.json();

      if (phase === "offseason") {
        setAgents(data.freeAgents || []);
        setCapInfo(data.capSpace || {});
        const rosterData = data.rosterBreakdown || {};
        setRosterInfo({
          totalRoster: rosterData.totalRoster || 0,
          byPosition: rosterData.byPosition || {}
        });
      } else if (phase === "regular_season") {
        setAgents(data.freeAgents || []);
        const capSpace = data.capSpace || {};
        setCapInfo({
          availableCap: capSpace.availableCap || 0,
          usedCap: capSpace.usedCap || 0,
          totalCap: capSpace.totalCap || 200
        });
        const rosterData = data.rosterBreakdown || {};
        setRosterInfo({
          totalRoster: data.teamRosterSize || 0,
          byPosition: rosterData.byPosition || {}
        });
      }
    } catch (e) {
      // Failed to load free agent status
    } finally {
      setLoading(false);
    }
  };

  // Load pending confirmation from localStorage on mount
  useEffect(() => {
    const storageKey = `pending_confirmation_${activeSaveId}`;
    const stored = localStorage.getItem(storageKey);
    if (stored) {
      setPendingConfirmation(stored);
    }
  }, [activeSaveId]);

  // Save pending confirmation to localStorage whenever it changes
  useEffect(() => {
    const storageKey = `pending_confirmation_${activeSaveId}`;
    if (pendingConfirmation) {
      localStorage.setItem(storageKey, pendingConfirmation);
    } else {
      localStorage.removeItem(storageKey);
    }
  }, [pendingConfirmation, activeSaveId]);

  // Clear pending confirmation when free agency phase ends
  useEffect(() => {
    if (gamePhase !== "offseason" && gamePhase !== "regular_season") {
      setPendingConfirmation(null);
      const storageKey = `pending_confirmation_${activeSaveId}`;
      localStorage.removeItem(storageKey);
    }
  }, [gamePhase, activeSaveId]);

  // Clear pending confirmation when user opens a different player's dropdown
  useEffect(() => {
    if (expandedPlayer && pendingConfirmation && expandedPlayer !== pendingConfirmation) {
      setPendingConfirmation(null);
      const storageKey = `pending_confirmation_${activeSaveId}`;
      localStorage.removeItem(storageKey);
    }
  }, [expandedPlayer, activeSaveId]);

  useEffect(() => {
    loadStatus();
    // Only poll while bidding is active, much less frequently
    let interval;
    if (isBiddingOpen) {
      interval = setInterval(loadStatus, 10000); // 10 seconds when bidding is open
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isBiddingOpen]);

  // Auto-trigger bidding on mount for offseason
  useEffect(() => {
    if (gamePhase === "offseason" && !isBiddingOpen && agents.length > 0) {
      const autoStartBidding = async () => {
        try {
          const res = await fetch(`${API_URL}/api/saves/${activeSaveId}/free-agency/generate-ai-bids`, {
            method: "POST",
            headers: { "X-User-ID": user._id, "Content-Type": "application/json" }
          });
          if (res.ok) {
            setIsBiddingOpen(true);
            await loadStatus();
          }
        } catch (e) {
          // Error auto-starting bidding
        }
      };
      autoStartBidding();
    }
  }, [gamePhase, agents.length]);

  const handleGenerateAIBids = async () => {
    try {
      setSubmitting({ ...submitting, ai: true });
      const res = await fetch(`${API_URL}/api/saves/${activeSaveId}/free-agency/generate-ai-bids`, {
        method: "POST",
        headers: { "X-User-ID": user._id, "Content-Type": "application/json" }
      });
      if (res.ok) {
        const data = await res.json();
        setIsBiddingOpen(true);
        loadStatus();
      }
    } finally {
      setSubmitting({ ...submitting, ai: false });
    }
  };

  const handleFinalizeBidding = async () => {
    const isOffseason = gamePhase === "offseason";

    try {
      setFinalizing(true);
      
      // Choose endpoint based on game phase
      let endpoint = isOffseason 
        ? `/api/saves/${activeSaveId}/free-agency/complete-offseason`
        : `/api/saves/${activeSaveId}/free-agency/finalize-bidding`;
      
      const res = await fetch(`${API_URL}${endpoint}`, {
        method: "POST",
        headers: { "X-User-ID": user._id, "Content-Type": "application/json" }
      });

      if (res.ok) {
        const data = await res.json();
        
        if (isOffseason) {
          // Show recap for offseason
          setRecapData(data);
        } else {
          // For mid-season finalize, auto-advance
          window.location.href = "/dashboard";
        }
      } else {
        // Error with finalize operation
      }
    } catch (e) {
      // Error with finalize operation
    } finally {
      setFinalizing(false);
    }
  };

  const handleSignPlayer = async (playerId, playerName, freeAgentIdForOffseason, salaryDemand) => {
    // Two-step confirmation: first click sets pending, second click confirms
    if (pendingConfirmation !== playerId) {
      setPendingConfirmation(playerId);
      return;
    }

    // Clear pending confirmation and proceed with signing
    setPendingConfirmation(null);

    if (gamePhase === "offseason") {
      try {
        setSubmitting({ ...submitting, [playerId]: true });
        const res = await fetch(
          `${API_URL}/api/saves/${activeSaveId}/free-agency/offseason-sign?player_id=${playerId}&free_agent_id=${freeAgentIdForOffseason}`,
          {
            method: "POST",
            headers: { "X-User-ID": user._id }
          }
        );

        if (res.ok) {
          const result = await res.json();
          setExpandedPlayer(null);
          loadStatus();
        } else {
          // Error signing player
        }
      } catch (e) {
        // Error signing player
      } finally {
        setSubmitting({ ...submitting, [playerId]: false });
      }
    } else if (gamePhase === "regular_season") {
      const STANDARD_SALARY = 2.4;
      const STANDARD_YEARS = 1;

      try {
        setSubmitting({ ...submitting, [playerId]: true });
        const res = await fetch(
          `${API_URL}/api/saves/${activeSaveId}/sign-free-agent?player_id=${playerId}&salary_per_year=${STANDARD_SALARY}&contract_years=${STANDARD_YEARS}`,
          {
            method: "POST",
            headers: { "X-User-ID": user._id }
          }
        );

        if (res.ok) {
          setExpandedPlayer(null);
          loadStatus();
        } else {
          // Error signing player
        }
      } catch (e) {
        // Error signing player
      } finally {
        setSubmitting({ ...submitting, [playerId]: false });
      }
    }
  };

  const positions = ["ALL", "PG", "SG", "SF", "PF", "C"];

  const filtered = agents.filter(p => {
    const name = (p.name || "").toLowerCase();
    const matchSearch = name.includes(search.toLowerCase());
    const matchPos = posFilter === "ALL" || p.position === posFilter;
    return matchSearch && matchPos;
  }).sort((a, b) => b.overall - a.overall);

  const capPercentage = capInfo ? Math.min(100, (capInfo.usedCap / capInfo.totalCap) * 100) : 0;

  // Calculate pending bids (user's active bids during bidding)
  const pendingBidTotal = isBiddingOpen ? agents.reduce((total, player) => {
    return total + (player.userBidAmount || 0);
  }, 0) : 0;

  const trulyAvailableCap = capInfo ? capInfo.availableCap - pendingBidTotal : 0;

  return (
    <div style={{ minHeight: "100vh", background: "var(--color-bg-main)" }}>
      <Navbar />
      
      <div style={{ maxWidth: "1400px", margin: "0 auto", padding: "2rem 1.5rem" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "2rem" }}>
          <div>
            <h1 style={{ fontSize: "2.5rem", fontWeight: "700", margin: 0, marginBottom: "0.5rem", color: "var(--color-text-main)" }}>
              Free Agency
            </h1>
            <p style={{ color: "var(--color-text-secondary)", margin: 0 }}>
              {isBiddingOpen ? (
                <span style={{ color: "#4ade80", fontWeight: "600" }}>🔴 Bidding Open</span>
              ) : (
                <span style={{ color: "var(--color-text-secondary)", fontWeight: "600" }}>⚫ Not Started</span>
              )}
              {" · "}{agents.length} players available
            </p>
          </div>
          <button 
            onClick={() => navigate("/dashboard")}
            style={{
              padding: "0.75rem 1.5rem",
              background: "var(--color-bg-container)",
              border: "1px solid var(--color-border-input)",
              borderRadius: "6px",
              cursor: "pointer",
              fontSize: "1rem",
              fontWeight: "600",
              color: "var(--color-text-main)"
            }}
          >
            Back to Dashboard
          </button>
        </div>

        {/* Cap Space & Roster Info Row */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "2rem", marginBottom: "2rem" }}>
          {/* Cap Space Card */}
          <div style={{
            background: "var(--color-bg-container)",
            borderRadius: "8px",
            padding: "1.5rem",
            boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
            border: "1px solid var(--color-border-input)"
          }}>
            <h3 style={{ margin: "0 0 1rem 0", fontSize: "1.1rem", fontWeight: "600", color: "var(--color-text-main)" }}>Salary Cap Space</h3>
            <div style={{
              background: "var(--color-textinput-background)",
              borderRadius: "4px",
              height: "24px",
              overflow: "hidden",
              marginBottom: "0.75rem"
            }}>
              <div style={{
                height: "100%",
                width: `${capPercentage}%`,
                background: capPercentage > 80 ? "#ef4444" : capPercentage > 50 ? "#f97316" : "#10b981",
                transition: "width 0.3s ease"
              }} />
            </div>
            <div style={{ fontSize: "0.9rem", color: "var(--color-text-secondary)", lineHeight: "1.5" }}>
              <div style={{ color: "var(--color-text-main)" }}><strong>${capInfo?.usedCap?.toFixed(1) || 0}M</strong> / ${capInfo?.totalCap || 200}M used</div>
              {isBiddingOpen && pendingBidTotal > 0 ? (
                <>
                  <div style={{ color: "#f97316", fontWeight: "600" }}>
                    ${pendingBidTotal.toFixed(1)}M in pending bids
                  </div>
                  <div style={{ color: "#ef4444", fontWeight: "600" }}>
                    ${trulyAvailableCap.toFixed(1)}M truly available
                  </div>
                </>
              ) : (
                <div style={{ color: "#10b981", fontWeight: "600" }}>
                  ${capInfo?.availableCap?.toFixed(1) || 0}M available
                </div>
              )}
            </div>
          </div>

          {/* Roster Breakdown Card */}
          <div style={{
            background: "var(--color-bg-container)",
            borderRadius: "8px",
            padding: "1.5rem",
            boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
            border: "1px solid var(--color-border-input)"
          }}>
            <h3 style={{ margin: "0 0 1rem 0", fontSize: "1.1rem", fontWeight: "600", color: "var(--color-text-main)" }}>Roster Breakdown</h3>
            <div style={{
              display: "grid",
              gridTemplateColumns: "repeat(5, 1fr)",
              gap: "0.75rem"
            }}>
              {["PG", "SG", "SF", "PF", "C"].map(pos => (
                <div key={pos} style={{
                  textAlign: "center",
                  padding: "0.75rem",
                  background: "var(--color-textinput-background)",
                  borderRadius: "4px"
                }}>
                  <div style={{ fontSize: "0.75rem", color: "var(--color-text-secondary)", marginBottom: "0.25rem", fontWeight: "600" }}>{pos}</div>
                  <div style={{ fontSize: "1.5rem", fontWeight: "700", color: "var(--color-text-main)" }}>
                    {rosterInfo?.byPosition?.[pos] || 0}
                  </div>
                </div>
              ))}
            </div>
            <div style={{ marginTop: "1rem", fontSize: "0.9rem", color: "var(--color-text-secondary)" }}>
              Total: <strong>{rosterInfo?.totalRoster || 0}/15</strong>
            </div>
          </div>
        </div>

        {/* Action Button - Only show Finalize when bidding is open */}
        {isBiddingOpen && (
          <div style={{ marginBottom: "2rem" }}>
            <button 
              className="fa-action-btn success"
              onClick={handleFinalizeBidding}
              disabled={finalizing}
              style={{
                width: "100%",
                padding: "1rem",
                background: "#4ade80",
                color: "#1a1a1a",
                border: "none",
                borderRadius: "6px",
                fontSize: "1.1rem",
                fontWeight: "600",
                cursor: finalizing ? "not-allowed" : "pointer",
                opacity: finalizing ? 0.7 : 1
              }}
            >
              {finalizing ? "Finalizing..." : "Finish Free Agency"}
            </button>
          </div>
        )}

        {/* Search & Filters */}
        <div style={{
          background: "var(--color-bg-container)",
          borderRadius: "8px",
          padding: "1.5rem",
          marginBottom: "2rem",
          boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
          border: "1px solid var(--color-border-input)"
        }}>
          <input
            className="fa-search"
            placeholder="Search players..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{
              width: "100%",
              padding: "0.75rem",
              border: "1px solid var(--color-border-input)",
              borderRadius: "6px",
              fontSize: "1rem",
              marginBottom: "1rem",
              boxSizing: "border-box",
              background: "var(--color-textinput-background)",
              color: "var(--color-text-main)"
            }}
          />
          <div className="fa-pos-filters" style={{
            display: "flex",
            gap: "0.75rem",
            flexWrap: "wrap"
          }}>
            {positions.map(pos => (
              <button
                key={pos}
                className={`fa-pos-btn ${posFilter === pos ? "active" : ""}`}
                onClick={() => setPosFilter(pos)}
                style={{
                  padding: "0.5rem 1rem",
                  background: posFilter === pos ? "var(--color-primary)" : "var(--color-bg-container)",
                  color: posFilter === pos ? "white" : "var(--color-text-secondary)",
                  border: posFilter === pos ? "1px solid var(--color-primary)" : "1px solid var(--color-border-input)",
                  borderRadius: "4px",
                  cursor: "pointer",
                  fontWeight: "600",
                  fontSize: "0.9rem"
                }}
              >
                {pos}
              </button>
            ))}
          </div>
        </div>

        {/* Players List */}
        <div style={{
          background: "var(--color-bg-container)",
          borderRadius: "8px",
          boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
          overflow: "hidden",
          border: "1px solid var(--color-border-input)"
        }}>
          {loading ? (
            <div style={{ padding: "3rem", textAlign: "center", color: "var(--color-text-secondary)" }}>
              <div>Loading...</div>
            </div>
          ) : filtered.length === 0 ? (
            <div style={{ padding: "3rem", textAlign: "center", color: "var(--color-text-secondary)" }}>
              No free agents found.
            </div>
          ) : (
            <div className="fa-list">
              {filtered.map(p => {
                const isExpanded = expandedPlayer === p._id;
                return (
                  <div key={p._id} className="fa-row-wrapper">
                    <div 
                      className="fa-row"
                      onClick={() => setExpandedPlayer(isExpanded ? null : p._id)}
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        padding: "1rem 1.5rem",
                        borderBottom: "1px solid var(--color-border-input)",
                        cursor: "pointer",
                        transition: "background 0.2s"
                      }}
                      onMouseOver={(e) => e.currentTarget.style.background = "rgba(18, 82, 210, 0.05)"}
                      onMouseOut={(e) => e.currentTarget.style.background = "transparent"}
                    >
                      <div className="fa-row-left" style={{ display: "flex", alignItems: "center", gap: "1rem", flex: 1 }}>
                        <div className="fa-avatar" style={{
                          width: "40px",
                          height: "40px",
                          background: "linear-gradient(135deg, var(--color-primary), var(--color-secondary))",
                          color: "white",
                          borderRadius: "4px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontWeight: "600"
                        }}>
                          {p.position || "?"}
                        </div>
                        <div className="fa-info">
                          <div style={{ fontSize: "1rem", fontWeight: "600", color: "var(--color-text-main)" }}>
                            {p.name}
                            {isBiddingOpen && p.salaryDemand && (
                              <span style={{ fontSize: "0.9rem", color: "#ef4444", marginLeft: "0.75rem", fontWeight: "600" }}>
                                ${p.salaryDemand}M/yr
                              </span>
                            )}
                          </div>
                          <div style={{ fontSize: "0.85rem", color: "var(--color-text-secondary)" }}>
                            {p.position || "N/A"}
                            {isBiddingOpen && p.experience && (
                              <span style={{ marginLeft: "0.75rem" }}>• {p.experience} yrs exp</span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="fa-row-right" style={{ display: "flex", alignItems: "center", gap: "2rem" }}>
                        <div className="fa-stat" style={{ textAlign: "center" }}>
                          <div style={{ fontSize: "0.75rem", color: "var(--color-text-secondary)" }}>OVR</div>
                          <div style={{
                            fontSize: "1.25rem",
                            fontWeight: "700",
                            color: p.overall >= 80 ? "#4ade80" : p.overall >= 65 ? "#facc15" : "#fb923c"
                          }}>
                            {p.overall}
                          </div>
                        </div>
                        {isBiddingOpen && (
                          <div className="fa-stat" style={{ textAlign: "center" }}>
                            <div style={{ fontSize: "0.75rem", color: "var(--color-text-secondary)" }}>Teams</div>
                            <div style={{ fontSize: "1.25rem", fontWeight: "700", color: "var(--color-primary)" }}>
                              {p.competingTeams || 0}
                            </div>
                          </div>
                        )}
                        <div style={{ fontSize: "1.25rem", color: "var(--color-text-secondary)" }}>
                          {isExpanded ? "▼" : "▶"}
                        </div>
                      </div>
                    </div>

                    {isExpanded && isBiddingOpen && (
                      <div style={{
                        padding: "1.5rem",
                        background: "rgba(18, 82, 210, 0.05)",
                        borderBottom: "1px solid var(--color-border-input)"
                      }}>
                        <div style={{ marginBottom: "1.5rem" }}>
                              <div style={{ marginBottom: "1rem" }}>
                                <div style={{ fontSize: "0.9rem", color: "var(--color-text-secondary)", marginBottom: "0.25rem" }}>Position</div>
                                <div style={{ fontSize: "1.1rem", fontWeight: "600", color: "var(--color-text-main)" }}>{p.position}</div>
                              </div>
                              <div style={{ marginBottom: "1rem" }}>
                                <div style={{ fontSize: "0.9rem", color: "var(--color-text-secondary)", marginBottom: "0.25rem" }}>Salary Demand</div>
                                <div style={{ fontSize: "1.25rem", fontWeight: "700", color: "#ef4444" }}>
                                  ${p.salaryDemand}M/year
                                </div>
                              </div>
                              <div>
                                <div style={{ fontSize: "0.9rem", color: "var(--color-text-secondary)", marginBottom: "0.25rem" }}>Experience</div>
                                <div style={{ fontSize: "1rem", color: "var(--color-text-main)" }}>{p.experience} years</div>
                              </div>
                            </div>
                            <button
                              onClick={() => handleSignPlayer(p._id, p.name, p.freeAgentId, p.salaryDemand)}
                              disabled={submitting[p._id]}
                              style={{
                                width: "100%",
                                padding: "0.75rem",
                                background: pendingConfirmation === p._id ? "#10b981" : "var(--color-primary)",
                                color: "white",
                                border: `2px solid ${pendingConfirmation === p._id ? "#10b981" : "var(--color-primary)"}`,
                                borderRadius: "4px",
                                cursor: submitting[p._id] ? "not-allowed" : "pointer",
                                fontWeight: "600",
                                opacity: submitting[p._id] ? 0.7 : 1,
                                transition: "all 0.2s ease"
                              }}
                              onMouseEnter={(e) => !submitting[p._id] && (e.target.style.background = pendingConfirmation === p._id ? "#059669" : "var(--color-secondary)")}
                              onMouseLeave={(e) => !submitting[p._id] && (e.target.style.background = pendingConfirmation === p._id ? "#10b981" : "var(--color-primary)")}
                            >
                              {submitting[p._id] ? "Signing..." : pendingConfirmation === p._id ? "Confirm Signing" : "Sign to Team"}
                            </button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
      
      {recapData && <FreeAgencyRecap recap={recapData} onDismiss={() => setRecapData(null)} />}
    </div>
  );
}